import { useEffect, useRef, useState } from 'react';
import { ShieldCheckIcon } from 'lucide-react';
import { demoTwoFactorHint, twoFactorChallenge } from '../adminApi';
import { btnPrimary, inputCls } from '../ui/styles';
import { errorMessage } from '../../core/errors';
import { DemoHintBox } from './DemoHintBox';
import type { AdminUser } from '../../core/types/adminRequests';
import type { DemoHint } from '../../core/types/backend';
type Props = {
  onDone: (user: AdminUser) => void;
  onBack: () => void;
};
const digitsOnly = (v: string) => v.replace(/\D/g, '').slice(0, 6);

/**
 * De tweede inlogstap: zes cijfers uit de authenticator-app, of een herstelcode als de
 * telefoon kwijt is. Zes cijfers versturen zichzelf, zodat Sarah niet hoeft te tikken.
 */
export function TwoFactorChallenge({
  onDone,
  onBack
}: Props) {
  const [recovery, setRecovery] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState<DemoHint | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const busyRef = useRef(false);
  useEffect(() => {
    inputRef.current?.focus();
  }, [recovery]);

  // DEMO: de code die de app nu toont, elke 5 s ververst. Met een backend is demoTwoFactorHint null.
  useEffect(() => {
    const load = demoTwoFactorHint;
    if (!load) return;
    let alive = true;
    const tick = () => void load().then(h => alive && setHint(h));
    tick();
    const timer = window.setInterval(tick, 5000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, []);
  const send = async (raw: string) => {
    if (busyRef.current) return;
    const v = raw.trim();
    if (!v) return setError(recovery ? 'Vul een herstelcode in.' : 'Vul de zes cijfers uit je app in.');
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      onDone(await twoFactorChallenge(recovery ? {
        recoveryCode: v
      } : {
        code: v
      }));
    } catch (err) {
      setError(errorMessage(err) ?? 'Dat lukte niet. Probeer het zo nog eens.');
      setValue('');
      busyRef.current = false;
      setBusy(false);
      inputRef.current?.focus();
    }
  };
  const onChange = (raw: string) => {
    const v = recovery ? raw : digitsOnly(raw);
    setValue(v);
    if (!recovery && v.length === 6) void send(v);
  };
  return <>
      <span className="mt-10 flex h-11 w-11 items-center justify-center rounded-full bg-shell text-clay ring-1 ring-line" aria-hidden="true" data-id="element-4049" data-mp="1zjax70knj8u7">
        <ShieldCheckIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-4050" data-mp="1pqwrjwqgdi2r" />
      </span>
      <h1 className="mt-5 font-display text-[2rem] font-light leading-tight text-ink" data-id="element-4051" data-mp="1krskbbn39uf2">
        {recovery ? 'Inloggen met een herstelcode' : 'Nog één stap'}
      </h1>
      <p className="mt-2 text-[14px] leading-relaxed text-muted" data-id="element-4052" data-mp="1godtdpxm3tmu">
        {recovery ? 'Gebruik een van de herstelcodes die je bij het instellen hebt bewaard. Elke code werkt één keer.' : 'Open je authenticator-app en vul de zes cijfers in die bij Beauty Bewust staan.'}
      </p>

      <form onSubmit={e => {
      e.preventDefault();
      void send(value);
    }} className="mt-7 space-y-4" noValidate data-id="element-4053" data-mp="3ihqobz1rui00">
        <label className="block" data-id="element-4054" data-mp="3sf54rynp36sb">
          <span className="text-[13px] font-medium text-ink" data-id="element-4055" data-mp="3sbzgglmw48xn">{recovery ? 'Herstelcode' : 'Code uit je app'}</span>
          <input ref={inputRef} value={value} onChange={e => onChange(e.target.value)} inputMode={recovery ? 'text' : 'numeric'} autoComplete={recovery ? 'off' : 'one-time-code'} autoCapitalize="off" spellCheck={false} maxLength={recovery ? 32 : 6} placeholder={recovery ? 'abcde-12345' : '123456'} aria-invalid={Boolean(error)} aria-describedby={error ? 'challenge-error' : undefined} disabled={busy} className={`${inputCls} mt-1.5 py-3 text-center font-mono tracking-[0.3em] sm:text-[18px]`} data-id="element-4056" data-mp="2zp50a0x3vvpz" />
        </label>

        {error && <p id="challenge-error" role="alert" className="rounded-md bg-blush/60 px-3 py-2 text-[13px] text-ink" data-id="element-4057" data-mp="1wwk8w5edj7bk">
            {error}
          </p>}

        <button type="submit" disabled={busy} className={`${btnPrimary} w-full py-3`} data-id="element-4058" data-mp="2bmzx7t236vdy">
          {busy ? 'Bezig met controleren…' : 'Inloggen'}
        </button>
      </form>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-2 text-[13px]" data-id="element-4059" data-mp="2kl3l2w0jo48x">
        <button type="button" onClick={() => {
        setRecovery(r => !r);
        setValue('');
        setError(null);
      }} className="py-2 text-clay underline-offset-4 hover:underline" data-id="element-4060" data-mp="2fntkck7pg65s">
          {recovery ? 'Toch de code uit je app gebruiken' : 'Telefoon niet bij de hand?'}
        </button>
        <button type="button" onClick={onBack} className="py-2 text-muted hover:text-ink" data-id="element-4061" data-mp="1084kwdqwx60k">
          Ander account
        </button>
      </div>

      {hint && !recovery && <DemoHintBox title="Demo" data-id="element-4062" data-mp="36v259qilng1b">
          {hint.label}: <span className="font-mono text-ink" data-id="element-4063" data-mp="3bu524nofefy7">{hint.value}</span>
        </DemoHintBox>}
    </>;
}